import React, { useRef, useCallback } from "react";
import {
  VStack,
  Box,
  Button,
  Text,
  Heading,
  Divider,
  Grid,
  GridItem,
  ListItem,
  UnorderedList,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { useField } from "formik";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";

import FileUploadPreview from "./FileUploadPreview";
import { SUPPORTING_DOCUMENTS_FILETYPES } from "../utils";

const FIELD_NAME = "documents.extra_files";
export default function SupportingDocuments() {
  const toast = useToast();
  const [{ value: documents }, _meta, { setValue }] = useField(FIELD_NAME);
  const inputRef = useRef();

  const handleAddDocument = useCallback(
    (e) => {
      const f = e.currentTarget.files[0];
      if (f.type.includes("image/") || f.type.includes("pdf")) {
        setValue([...documents, f]);
        inputRef.current.value = "";
      } else {
        /**
         * This will only fire if the user changes which file they've selected
         * as <input type="file" /> is fully user controlled and the "change" event
         * won't fire if the file the user selected hasn't changed.
         */
        toast({
          title: "Unsupported file type",
          description: "Please select a jpg or png image file, or a pdf file.",
          status: "error",
          isClosable: true,
        });
      }
    },
    [documents, setValue, toast]
  );

  const handleRemoveDocument = useCallback(
    (index) => {
      setValue([...documents.slice(0, index), ...documents.slice(index + 1)]);
    },
    [documents, setValue]
  );

  return (
    <>
      <Divider />
      <VStack alignItems="stretch">
        <Heading color="secondaryText" fontSize="md">
          Supporting documents
        </Heading>
        <Text>
          Please provide at least one proof of address document issued within the last 90 days that
          includes your name and current residential address. It can be one of the following:
        </Text>
        <UnorderedList pl={4}>
          <ListItem>Utility bill</ListItem>
          <ListItem>Bank statement</ListItem>
          <ListItem>Phone or internet bill</ListItem>
          <ListItem>Rental agreement</ListItem>
          <ListItem>Voter registration</ListItem>
          <ListItem>Insurance statement</ListItem>
        </UnorderedList>
      </VStack>

      <VStack alignItems="stretch">
        <Button
          onClick={() => inputRef.current.click()}
          leftIcon={<AddIcon />}
          variant="outline"
          isDisabled={documents.length >= 4}
          aria-label="Add a supporting document"
          width="full"
        >
          Add a document
        </Button>
        <input
          type="file"
          accept={SUPPORTING_DOCUMENTS_FILETYPES}
          name="supporting-document"
          ref={inputRef}
          onChange={handleAddDocument}
          style={{ display: "none" }}
          data-testid="supporting-document-upload-hidden-input"
        />

        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
          {documents.map((file, index) => {
            const name = `${FIELD_NAME}.${index}`;
            const fileName = file?.name || "Unknown file name";
            return (
              <GridItem
                position="relative"
                borderColor="integrator.100"
                borderWidth={1}
                h={180}
                w="100%"
                borderRadius={8}
                overflow="hidden"
                key={`supporting-document-${index}`}
              >
                <IconButton
                  onClick={() => handleRemoveDocument(index)}
                  icon={<CloseIcon />}
                  position="absolute"
                  top={2}
                  right={2}
                  rounded="full"
                  size="sm"
                  aria-label={`Remove supporting document named ${fileName}`}
                />
                <Box position="absolute" bottom={0} left={0} right={0} p={2} bg="integrator.100">
                  <Text fontSize="sm" color="integrator.500" noOfLines={1}>
                    {fileName}
                  </Text>
                </Box>
                <FileUploadPreview
                  name={name}
                  position="absolute"
                  top={0}
                  right={0}
                  bottom={0}
                  left={0}
                  width="100%"
                  height="100%"
                  zIndex={-1}
                  objectFit="cover"
                />
              </GridItem>
            );
          })}
        </Grid>
      </VStack>
    </>
  );
}
